// @tennis-matches/services/governance
// Effektive sichtbare Apps (Phase-1-Governance) — shared Web + Mobile.

import { isGlobalSuperadminForSession } from "./global-superadmin-session.js";
import {
  isFamilyOnlyRoleBindings,
  restrictEffectiveVisibleAppsForFamilyOnly,
} from "./role-viewer-mode.js";

/** Phase-1 access-controlled keys (Spiegelung apps/web app-catalog, inAllAppKeysPhase1). */
export const ACCESS_CONTROLLED_APP_KEYS_PHASE1 = Object.freeze([
  "matchmixer",
  "trainerstunden",
  "trainer",
  "clubmeisterschaft",
  "medenspiele",
  "belegung",
  "winterdoppel",
  "kalender",
  "insights",
  "clubhub",
  "minigames",
  "tennis-live",
]);

/** Nur MatchFlow-Staff / Superadmin — nicht Vereins-Marketplace. */
export const STAFF_ONLY_APP_KEYS = Object.freeze(["matchflow"]);

/** Hub-Marketplace-Apps (showOnGlobalHub, ohne shellRoute). */
export const HUB_MARKETPLACE_APP_KEYS = Object.freeze([
  "matchmixer",
  "trainerstunden",
  "trainer",
  "clubmeisterschaft",
  "medenspiele",
  "belegung",
  "winterdoppel",
  "kalender",
  "insights",
  "clubhub",
  "minigames",
  "tennis-live",
]);

const __ALL_APP_KEYS_SET = new Set(
  [...ACCESS_CONTROLLED_APP_KEYS_PHASE1, ...STAFF_ONLY_APP_KEYS]
    .map((k) => String(k).trim().toLowerCase())
    .filter(Boolean),
);
const __MARKETPLACE_SET = new Set(
  HUB_MARKETPLACE_APP_KEYS.map((k) => String(k).trim().toLowerCase()).filter(
    Boolean,
  ),
);

function __asStr(v) {
  return String(v ?? "").trim();
}

/**
 * Legacy DB/Governance-Keys → kanonischer Katalog-Key.
 * @param {string} appKey
 */
export function normalizeGovernanceAppKey(appKey) {
  const k = __asStr(appKey).toLowerCase();
  if (k === "schleifchen" || k === "schleifchenturnier") return "matchmixer";
  if (k === "clubchamp") return "clubmeisterschaft";
  if (k === "teamdesk") return "medenspiele";
  if (k === "statistik") return "insights";
  return k;
}

function __hubMarketplaceKeySet() {
  return __MARKETPLACE_SET;
}

function __staffOnlyKeySet() {
  return new Set(
    STAFF_ONLY_APP_KEYS.map((k) => String(k).trim().toLowerCase()).filter(
      Boolean,
    ),
  );
}

/** @param {unknown[]} roleBindings */
function __hasMatchflowOperatorBinding(roleBindings) {
  for (const b of Array.isArray(roleBindings) ? roleBindings : []) {
    if (b?.is_active === false) continue;
    if (__asStr(b?.role).toLowerCase() === "matchflow_operator") return true;
  }
  return false;
}

/**
 * @param {Set<string>} allowed
 * @param {unknown[]} roleBindings
 * @param {unknown} authUser
 */
function __grantStaffOnlyApps(allowed, roleBindings, authUser) {
  const staff = __staffOnlyKeySet();
  const grant =
    isGlobalSuperadminForSession(roleBindings, authUser) ||
    __hasMatchflowOperatorBinding(roleBindings);
  if (!grant) return;
  for (const k of staff) allowed.add(k);
}

function __rolesAtTenantScope(roleBindings, tenantScopeId) {
  const sid = __asStr(tenantScopeId);
  const roles = new Set();
  for (const b of Array.isArray(roleBindings) ? roleBindings : []) {
    if (__asStr(b?.scope).toLowerCase() !== "tenant") continue;
    if (__asStr(b?.scope_id) !== sid) continue;
    if (b?.is_active === false) continue;
    const role = __asStr(b?.role).toLowerCase();
    if (role) roles.add(role);
  }
  return roles;
}

/** Mindestens eine aktive Tenant-Rollenbindung. */
export function hasActiveTenantRoleBindings(roleBindings) {
  for (const b of Array.isArray(roleBindings) ? roleBindings : []) {
    if (b?.is_active === false) continue;
    if (__asStr(b?.scope).toLowerCase() !== "tenant") continue;
    if (!__asStr(b?.scope_id)) continue;
    if (__asStr(b?.role)) return true;
  }
  return false;
}

const __STAFF_TRAINER_ROLES = new Set([
  "trainer",
  "trainer_coordinator",
  "jugendwart",
]);

function __hasStaffTrainerRole(roles) {
  const rset =
    roles instanceof Set ? roles : new Set(Array.isArray(roles) ? roles : []);
  return Array.from(rset).some((r) => __STAFF_TRAINER_ROLES.has(r));
}

/** TrainingsDesk + Trainer Studio gehören für Staff-Trainer zusammen. */
function __syncStaffTrainerMarketplaceApps(
  rolesAtScope,
  allowed,
  accessSet,
  deniedMarketplace = new Set(),
) {
  if (!__hasStaffTrainerRole(rolesAtScope)) return;
  const denied =
    deniedMarketplace instanceof Set ? deniedMarketplace : new Set();
  if (
    allowed.has("trainer") &&
    !denied.has("trainerstunden") &&
    accessSet.has("trainerstunden")
  ) {
    allowed.add("trainerstunden");
  }
  if (
    allowed.has("trainerstunden") &&
    !denied.has("trainer") &&
    accessSet.has("trainer")
  ) {
    allowed.add("trainer");
  }
}

function __fallbackAppsForRoles(roles) {
  const rset =
    roles instanceof Set ? roles : new Set(Array.isArray(roles) ? roles : []);
  const adminRoles = new Set(["admin", "vereinsadmin", "vorstand", "owner"]);
  if (Array.from(rset).some((r) => adminRoles.has(r))) {
    return {
      ok: true,
      allApps: true,
      allowedAppKeys: new Set(ACCESS_CONTROLLED_APP_KEYS_PHASE1),
    };
  }
  const allowed = new Set();
  if (
    rset.has("trainer") ||
    rset.has("trainer_coordinator") ||
    rset.has("jugendwart")
  ) {
    allowed.add("trainerstunden");
    allowed.add("trainer");
    allowed.add("belegung");
    allowed.add("kalender");
  }
  if (rset.has("kassenwart")) {
    allowed.add("clubhub");
  }
  if (rset.has("sportwart")) {
    allowed.add("matchmixer");
    allowed.add("clubmeisterschaft");
    allowed.add("medenspiele");
    allowed.add("belegung");
    allowed.add("winterdoppel");
    allowed.add("kalender");
  }
  if (rset.has("event_organizer")) {
    allowed.add("winterdoppel");
    allowed.add("kalender");
    allowed.add("matchmixer");
    allowed.add("clubmeisterschaft");
  }
  if (rset.has("team_captain")) {
    allowed.add("medenspiele");
  }
  if (rset.has("parent") || rset.has("player")) {
    allowed.add("trainerstunden");
    allowed.add("belegung");
    allowed.add("winterdoppel");
    allowed.add("kalender");
  }
  return { ok: true, allApps: false, allowedAppKeys: allowed };
}

export function resolveRoleAppAccessMatrixCell(
  role,
  appKey,
  mode,
  scopeId,
  rows,
) {
  const roleKey = __asStr(role).toLowerCase();
  const app = normalizeGovernanceAppKey(appKey);
  const rk = `${roleKey}|${app}`;
  const list = Array.isArray(rows) ? rows : [];
  const m = __asStr(mode).toLowerCase();
  const sid = m === "global" ? "global" : __asStr(scopeId);

  const row = list.find((r) => {
    if (__asStr(r?.scope).toLowerCase() !== m) return false;
    if (__asStr(r?.scope_id) !== sid) return false;
    return (
      `${__asStr(r?.role).toLowerCase()}|${normalizeGovernanceAppKey(r?.app_key)}` ===
      rk
    );
  });

  if (!row && m === "tenant") {
    const globalRow = list.find((r) => {
      if (__asStr(r?.scope).toLowerCase() !== "global") return false;
      if (__asStr(r?.scope_id) !== "global") return false;
      return (
        `${__asStr(r?.role).toLowerCase()}|${normalizeGovernanceAppKey(r?.app_key)}` ===
        rk
      );
    });
    if (globalRow) {
      const globalOn = globalRow.enabled !== false;
      return {
        checked: globalOn,
        isLocalOverride: false,
        globalEnabled: globalOn,
        hasData: true,
      };
    }
  }

  if (!row) {
    return {
      checked: false,
      isLocalOverride: false,
      globalEnabled: false,
      hasData: false,
    };
  }

  return {
    checked: row.enabled !== false,
    isLocalOverride: Boolean(row.is_local_override),
    globalEnabled: row.global_enabled === true,
    hasData: true,
  };
}

export function resolveRoleAppAccessEffectiveAtTenant(
  role,
  appKey,
  tenantScopeId,
  rows,
) {
  const roleKey = __asStr(role).toLowerCase();
  const app = normalizeGovernanceAppKey(appKey);
  const sid = __asStr(tenantScopeId);
  const list = Array.isArray(rows) ? rows : [];
  const rk = `${roleKey}|${app}`;

  const tenantCell = resolveRoleAppAccessMatrixCell(
    role,
    appKey,
    "tenant",
    tenantScopeId,
    rows,
  );
  if (tenantCell.hasData) return tenantCell.checked;

  const tenantMerged = list.find((r) => {
    if (__asStr(r?.scope).toLowerCase() !== "tenant") return false;
    if (__asStr(r?.scope_id) !== sid) return false;
    return (
      `${__asStr(r?.role).toLowerCase()}|${normalizeGovernanceAppKey(r?.app_key)}` ===
      rk
    );
  });

  if (
    tenantMerged &&
    tenantMerged.global_enabled !== undefined &&
    tenantMerged.global_enabled !== null
  ) {
    if (tenantMerged.global_enabled !== true) return false;
    return tenantMerged.enabled !== false;
  }

  const globalCell = resolveRoleAppAccessMatrixCell(
    role,
    appKey,
    "global",
    "global",
    rows,
  );
  return globalCell.checked;
}

export function resolveRoleAppAccessEffectiveAtGlobal(role, appKey, rows) {
  const cell = resolveRoleAppAccessMatrixCell(
    role,
    appKey,
    "global",
    "global",
    rows,
  );
  // Globaler Superadmin: fehlende Matrix-Zeile = erlaubt; nur explizit disabled sperrt.
  if (__asStr(role).toLowerCase() === "superadmin") {
    if (!cell.hasData) return true;
    return cell.checked === true;
  }
  return cell.checked;
}

export function deriveGovernedMarketplaceAppKeys(
  roleBindings,
  roleAppAccessRows,
  authUser = null,
) {
  const list = Array.isArray(roleBindings) ? roleBindings : [];
  const rows = Array.isArray(roleAppAccessRows) ? roleAppAccessRows : [];
  const marketplace = __hubMarketplaceKeySet();
  const allowed = new Set();

  const byTenant = new Map();
  for (const b of list) {
    const scope = __asStr(b?.scope).toLowerCase();
    const scopeId = __asStr(b?.scope_id);
    const role = __asStr(b?.role).toLowerCase();
    if (scope !== "tenant" || !scopeId || !role) continue;
    const prev = byTenant.get(scopeId) || new Set();
    prev.add(role);
    byTenant.set(scopeId, prev);
  }

  for (const [scopeId, roles] of byTenant) {
    for (const role of roles) {
      for (const appKey of marketplace) {
        if (
          resolveRoleAppAccessEffectiveAtTenant(role, appKey, scopeId, rows)
        ) {
          allowed.add(appKey);
        }
      }
    }
  }

  const globalRoles = new Set();
  for (const b of list) {
    const scope = __asStr(b?.scope).toLowerCase();
    const scopeId = __asStr(b?.scope_id);
    const role = __asStr(b?.role).toLowerCase();
    if (scope === "global" && scopeId === "global" && role)
      globalRoles.add(role);
  }
  if (isGlobalSuperadminForSession(list, authUser)) {
    globalRoles.add("superadmin");
  }
  for (const role of globalRoles) {
    for (const appKey of marketplace) {
      if (resolveRoleAppAccessEffectiveAtGlobal(role, appKey, rows)) {
        allowed.add(appKey);
      }
    }
  }

  return allowed;
}

function __deriveNonMarketplaceAppKeysFromBindings(
  roleBindings,
  authUser = null,
) {
  const list = Array.isArray(roleBindings) ? roleBindings : [];
  const roles = new Set(
    list.map((b) => __asStr(b?.role).toLowerCase()).filter(Boolean),
  );
  const marketplace = __hubMarketplaceKeySet();
  const allowed = new Set();

  const adminRoles = new Set(["admin", "vereinsadmin", "vorstand", "owner"]);
  if (Array.from(roles).some((r) => adminRoles.has(r))) {
    const staff = __staffOnlyKeySet();
    for (const k of ACCESS_CONTROLLED_APP_KEYS_PHASE1) {
      if (!marketplace.has(k) && !staff.has(k)) allowed.add(k);
    }
    return allowed;
  }

  const fb = __fallbackAppsForRoles(roles);
  for (const k of fb.allowedAppKeys || []) {
    if (!marketplace.has(k)) allowed.add(k);
  }
  return allowed;
}

export function deriveAllowedAppKeysFromRoleBindingsAndRoleAppAccess(
  roleBindings,
  roleAppAccessRows,
  authUser = null,
) {
  const list = Array.isArray(roleBindings) ? roleBindings : [];
  const rows = Array.isArray(roleAppAccessRows) ? roleAppAccessRows : [];

  const marketplace = deriveGovernedMarketplaceAppKeys(list, rows, authUser);
  const allowed = new Set(marketplace);

  for (const k of __deriveNonMarketplaceAppKeysFromBindings(list, authUser)) {
    allowed.add(k);
  }

  if (allowed.size === 0 && hasActiveTenantRoleBindings(list)) {
    const roles = new Set();
    for (const b of list) {
      if (__asStr(b?.scope).toLowerCase() !== "tenant") continue;
      if (b?.is_active === false) continue;
      const role = __asStr(b?.role).toLowerCase();
      if (role) roles.add(role);
    }
    const fb = __fallbackAppsForRoles(roles);
    for (const appKey of fb.allowedAppKeys || []) allowed.add(appKey);
  }

  return { ok: true, allApps: false, allowedAppKeys: allowed };
}

export function deriveAllowedAppKeysFromBindingsEntitlementsAndRoleAppAccess(
  roleBindings,
  entitlements,
  roleAppAccessRows,
  authUser = null,
) {
  const base = deriveAllowedAppKeysFromRoleBindingsAndRoleAppAccess(
    roleBindings,
    roleAppAccessRows,
    authUser,
  );
  if (!base?.ok)
    return { ok: false, allApps: false, allowedAppKeys: new Set() };
  if (base.allApps) return base;
  const allowed = new Set(base.allowedAppKeys || []);
  const list = Array.isArray(roleBindings) ? roleBindings : [];
  const rows = Array.isArray(roleAppAccessRows) ? roleAppAccessRows : [];
  const marketplace = __hubMarketplaceKeySet();
  const userId = __asStr(authUser?.id);
  const deniedMarketplace = new Set();

  for (const e of Array.isArray(entitlements) ? entitlements : []) {
    const app = normalizeGovernanceAppKey(e?.app_key);
    if (!app) continue;
    if (!marketplace.has(app)) {
      allowed.add(app);
      continue;
    }
    if (userId && __asStr(e?.user_id) !== userId) continue;
    const scope = __asStr(e?.scope).toLowerCase();
    const scopeId = __asStr(e?.scope_id);
    if (scope !== "tenant" || !scopeId) continue;
    if (e?.enabled === false) {
      deniedMarketplace.add(app);
      continue;
    }
    const rolesAtScope = __rolesAtTenantScope(list, scopeId);
    const globalOk = Array.from(rolesAtScope).some(
      (role) =>
        resolveRoleAppAccessMatrixCell(role, app, "global", "global", rows)
          .checked,
    );
    if (globalOk) allowed.add(app);
  }
  for (const app of deniedMarketplace) {
    allowed.delete(app);
  }
  return { ...base, allowedAppKeys: allowed };
}

export function deriveScopeAvailableAppKeys({
  scope = "tenant",
  scopeId,
  appManifests,
  isLoaded = false,
} = {}) {
  const normalizedScope = __asStr(scope).toLowerCase();
  const normalizedScopeId = __asStr(scopeId);
  const manifestsLoaded = Boolean(isLoaded);

  if (!manifestsLoaded || !Array.isArray(appManifests)) {
    return {
      ok: false,
      loaded: false,
      reason: "scope-availability-not-loaded",
      availableAppKeys: new Set(),
    };
  }

  const available = new Set();
  for (const row of appManifests) {
    const appKey = normalizeGovernanceAppKey(row?.id || row?.app_id);
    if (!appKey || !__ALL_APP_KEYS_SET.has(appKey)) continue;
    const tenantId = __asStr(row?.tenantId || row?.tenant_id);
    const isGlobalManifest = !tenantId;

    if (normalizedScope === "global") {
      if (isGlobalManifest) available.add(appKey);
      continue;
    }

    if (
      isGlobalManifest ||
      (normalizedScopeId && tenantId === normalizedScopeId)
    ) {
      available.add(appKey);
    }
  }

  if (normalizedScope === "global" || normalizedScope === "tenant") {
    for (const appKey of HUB_MARKETPLACE_APP_KEYS) {
      if (__ALL_APP_KEYS_SET.has(appKey)) available.add(appKey);
    }
  }
  if (normalizedScope === "global") {
    for (const appKey of STAFF_ONLY_APP_KEYS) {
      if (__ALL_APP_KEYS_SET.has(appKey)) available.add(appKey);
    }
  }

  return {
    ok: true,
    loaded: true,
    scope: normalizedScope || "tenant",
    scopeId: normalizedScopeId,
    availableAppKeys: available,
  };
}

function __toLowerSet(values) {
  const out = new Set();
  const list =
    values instanceof Set
      ? Array.from(values)
      : Array.isArray(values)
        ? values
        : [];
  for (const v of list) {
    const s = __asStr(v).toLowerCase();
    if (s) out.add(s);
  }
  return out;
}

function __deriveNonMarketplaceAppKeysForRoles(roles, marketplace) {
  const rset =
    roles instanceof Set ? roles : new Set(Array.isArray(roles) ? roles : []);
  const allowed = new Set();
  const adminRoles = new Set(["admin", "vereinsadmin", "vorstand", "owner"]);
  if (Array.from(rset).some((r) => adminRoles.has(r))) {
    const staff = __staffOnlyKeySet();
    for (const k of ACCESS_CONTROLLED_APP_KEYS_PHASE1) {
      if (!marketplace.has(k) && !staff.has(k)) allowed.add(k);
    }
    return allowed;
  }
  const fb = __fallbackAppsForRoles(rset);
  for (const k of fb.allowedAppKeys || []) {
    if (!marketplace.has(k)) allowed.add(k);
  }
  return allowed;
}

function __applyEntitlementsForTenantScope({
  allowed,
  entitlements,
  roleBindings,
  roleAppAccessRows,
  tenantScopeId,
  authUser,
  marketplace,
}) {
  const list = Array.isArray(roleBindings) ? roleBindings : [];
  const rows = Array.isArray(roleAppAccessRows) ? roleAppAccessRows : [];
  const sid = __asStr(tenantScopeId);
  const userId = __asStr(authUser?.id);
  const deniedMarketplace = new Set();

  for (const e of Array.isArray(entitlements) ? entitlements : []) {
    const app = normalizeGovernanceAppKey(e?.app_key);
    if (!app) continue;
    if (!marketplace.has(app)) {
      allowed.add(app);
      continue;
    }
    if (userId && __asStr(e?.user_id) !== userId) continue;
    const scope = __asStr(e?.scope).toLowerCase();
    const scopeId = __asStr(e?.scope_id);
    if (scope !== "tenant" || scopeId !== sid) continue;
    if (e?.enabled === false) {
      deniedMarketplace.add(app);
      continue;
    }
    const rolesAtScope = __rolesAtTenantScope(list, sid);
    const globalOk = Array.from(rolesAtScope).some(
      (role) =>
        resolveRoleAppAccessMatrixCell(role, app, "global", "global", rows)
          .checked,
    );
    if (globalOk) allowed.add(app);
  }
  for (const app of deniedMarketplace) {
    allowed.delete(app);
  }
  return deniedMarketplace;
}

/**
 * Rollen-/Matrix-Rechte nur für den angefragten Scope (nicht Session-Union).
 */
export function deriveUserAllowedAppKeysForScope({
  scope = "tenant",
  scopeId,
  roleBindings = [],
  entitlements = [],
  roleAppAccessRows = [],
  authUser = null,
  marketplaceAppKeys = HUB_MARKETPLACE_APP_KEYS,
  accessControlledAppKeys = ACCESS_CONTROLLED_APP_KEYS_PHASE1,
} = {}) {
  const normalizedScope = __asStr(scope).toLowerCase();
  const sid = __asStr(scopeId);
  const list = Array.isArray(roleBindings) ? roleBindings : [];
  const rows = Array.isArray(roleAppAccessRows) ? roleAppAccessRows : [];
  const marketplace = __toLowerSet(marketplaceAppKeys);
  const accessSet = __toLowerSet(accessControlledAppKeys);
  const allowed = new Set();

  if (normalizedScope === "global") {
    const globalRoles = new Set();
    for (const b of list) {
      if (__asStr(b?.scope).toLowerCase() !== "global") continue;
      if (__asStr(b?.scope_id) !== "global") continue;
      if (b?.is_active === false) continue;
      const role = __asStr(b?.role).toLowerCase();
      if (role) globalRoles.add(role);
    }
    if (isGlobalSuperadminForSession(list, authUser)) {
      globalRoles.add("superadmin");
    }
    for (const role of globalRoles) {
      for (const appKey of marketplace) {
        if (resolveRoleAppAccessEffectiveAtGlobal(role, appKey, rows)) {
          allowed.add(appKey);
        }
      }
    }
    for (const k of __deriveNonMarketplaceAppKeysForRoles(
      globalRoles,
      marketplace,
    )) {
      if (accessSet.has(k)) allowed.add(k);
    }
    __grantStaffOnlyApps(allowed, list, authUser);
    return allowed;
  }

  if (!sid) return allowed;

  const rolesAtScope = __rolesAtTenantScope(list, sid);
  for (const role of rolesAtScope) {
    for (const appKey of marketplace) {
      if (resolveRoleAppAccessEffectiveAtTenant(role, appKey, sid, rows)) {
        allowed.add(appKey);
      }
    }
  }
  for (const k of __deriveNonMarketplaceAppKeysForRoles(
    rolesAtScope,
    marketplace,
  )) {
    if (accessSet.has(k)) allowed.add(k);
  }
  const deniedMarketplace = __applyEntitlementsForTenantScope({
    allowed,
    entitlements,
    roleBindings: list,
    roleAppAccessRows: rows,
    tenantScopeId: sid,
    authUser,
    marketplace,
  });

  __syncStaffTrainerMarketplaceApps(
    rolesAtScope,
    allowed,
    accessSet,
    deniedMarketplace,
  );

  if (allowed.size === 0 && rolesAtScope.size > 0) {
    const fb = __fallbackAppsForRoles(rolesAtScope);
    for (const appKey of fb.allowedAppKeys || []) {
      if (accessSet.has(appKey)) allowed.add(appKey);
    }
  }

  __grantStaffOnlyApps(allowed, list, authUser);

  return allowed;
}

export function getEffectiveVisibleAppsForScope({
  scope = "tenant",
  scopeId,
  appManifests,
  isManifestsLoaded = false,
  roleBindings,
  entitlements,
  roleAppAccessRows,
  isFeatureEnabled = null,
  authUser = null,
} = {}) {
  const catalogKeys = __toLowerSet([
    ...ACCESS_CONTROLLED_APP_KEYS_PHASE1,
    ...STAFF_ONLY_APP_KEYS,
  ]);
  const availability = deriveScopeAvailableAppKeys({
    scope,
    scopeId,
    appManifests,
    isLoaded: isManifestsLoaded,
  });

  if (availability?.loaded !== true) {
    return {
      ok: false,
      loaded: false,
      reason: "scope-availability-not-loaded",
      effectiveVisibleAppKeys: new Set(),
      globallyAllowedAppKeys: new Set(),
      userAllowedAppKeys: new Set(),
      allApps: false,
    };
  }

  const scopeAvailable = __toLowerSet(availability?.availableAppKeys);
  const featureEnabled = new Set();
  for (const appKey of catalogKeys) {
    const enabled =
      typeof isFeatureEnabled === "function"
        ? isFeatureEnabled(appKey) !== false
        : true;
    if (enabled) featureEnabled.add(appKey);
  }

  const globallyAllowed = new Set();
  for (const appKey of catalogKeys) {
    if (scopeAvailable.has(appKey) && featureEnabled.has(appKey))
      globallyAllowed.add(appKey);
  }

  const userAllowedRaw = __toLowerSet(
    deriveUserAllowedAppKeysForScope({
      scope,
      scopeId,
      roleBindings,
      entitlements,
      roleAppAccessRows,
      authUser,
    }),
  );

  const effective = new Set();
  for (const appKey of userAllowedRaw) {
    if (globallyAllowed.has(appKey)) effective.add(appKey);
  }

  // TrainingsDesk-Zugang → Trainer Studio (Staff; nicht reines Eltern/Spieler-Profil)
  if (
    effective.has("trainerstunden") &&
    globallyAllowed.has("trainer") &&
    !isFamilyOnlyRoleBindings(roleBindings)
  ) {
    effective.add("trainer");
  }

  // Staff-only Apps (MatchFlow): Superadmin / matchflow_operator
  __grantStaffOnlyApps(effective, roleBindings, authUser);
  for (const k of __staffOnlyKeySet()) {
    if (effective.has(k) && !globallyAllowed.has(k)) {
      // Global-Scope Availability fehlt ggf. → trotzdem freigeben wenn Feature an
      const enabled =
        typeof isFeatureEnabled === "function"
          ? isFeatureEnabled(k) !== false
          : true;
      if (!enabled) effective.delete(k);
    }
  }

  return {
    ok: true,
    loaded: true,
    allApps: false,
    globallyAllowedAppKeys: globallyAllowed,
    userAllowedAppKeys: userAllowedRaw,
    effectiveVisibleAppKeys: effective,
  };
}

/**
 * Effektive App-Keys für die Session (Union aller Tenant-Scopes + optional Global/Superadmin).
 * @returns {Set<string>}
 */
export function resolveSessionEffectiveVisibleAppKeys({
  roleBindings = [],
  entitlements = [],
  roleAppAccessRows = [],
  appManifests = [],
  manifestsLoaded = false,
  authUser = null,
  isFeatureEnabled = null,
  /** Mobile: Rollen-Fallback wenn Manifeste noch nicht geladen. */
  roleFallbackWhenManifestsMissing = false,
} = {}) {
  const bindings = Array.isArray(roleBindings) ? roleBindings : [];
  const entitlementsList = Array.isArray(entitlements) ? entitlements : [];
  const rows = Array.isArray(roleAppAccessRows) ? roleAppAccessRows : [];
  const manifests = Array.isArray(appManifests) ? appManifests : [];

  if (!manifestsLoaded && roleFallbackWhenManifestsMissing) {
    const derived =
      deriveAllowedAppKeysFromBindingsEntitlementsAndRoleAppAccess(
        bindings,
        entitlementsList,
        rows,
        authUser,
      );
    if (derived?.allApps) {
      return new Set(ACCESS_CONTROLLED_APP_KEYS_PHASE1);
    }
    return new Set(derived?.allowedAppKeys || []);
  }

  if (!manifestsLoaded) {
    return new Set();
  }

  const tenantScopeIds = Array.from(
    new Set(
      bindings
        .filter((b) => __asStr(b?.scope).toLowerCase() === "tenant")
        .map((b) => __asStr(b?.scope_id))
        .filter(Boolean),
    ),
  );

  const hasGlobalSuperadmin = isGlobalSuperadminForSession(bindings, authUser);

  const out = new Set();
  const featureEnabled = (appKey) =>
    typeof isFeatureEnabled === "function"
      ? isFeatureEnabled(appKey) !== false
      : true;

  // Globaler Superadmin: nur globale Governance-Matrix (kein Tenant-Union).
  if (hasGlobalSuperadmin) {
    const effectiveGlobal = getEffectiveVisibleAppsForScope({
      scope: "global",
      scopeId: "global",
      appManifests: manifests,
      isManifestsLoaded: true,
      roleBindings: bindings,
      entitlements: entitlementsList,
      roleAppAccessRows: rows,
      isFeatureEnabled: featureEnabled,
      authUser,
    });
    for (const key of effectiveGlobal?.effectiveVisibleAppKeys || [])
      out.add(key);
    return restrictEffectiveVisibleAppsForFamilyOnly(out, bindings);
  }

  for (const sid of tenantScopeIds) {
    const effective = getEffectiveVisibleAppsForScope({
      scope: "tenant",
      scopeId: sid,
      appManifests: manifests,
      isManifestsLoaded: true,
      roleBindings: bindings,
      entitlements: entitlementsList,
      roleAppAccessRows: rows,
      isFeatureEnabled: featureEnabled,
      authUser,
    });
    for (const key of effective?.effectiveVisibleAppKeys || []) out.add(key);
  }

  return restrictEffectiveVisibleAppsForFamilyOnly(out, bindings);
}
